import posthog from 'posthog-js';
import { PostHogProvider as BasePostHogProvider, usePostHog } from 'posthog-js/react';
import { ReactNode } from 'react';
import { PosthogFeatureFlag } from '../../types/posthog-feature-flag.enum';
import { config } from '../../utils/config';
import { PosthogLoadingWrapper } from './PosthogLoadingWrapper';

const POSTHOG_KEY = config.env.VITE_PUBLIC_POSTHOG_KEY;
const POSTHOG_HOST = config.env.VITE_PUBLIC_POSTHOG_HOST;
const FEATURE_FLAG_CACHE_KEY = 'posthog_feature_flags_cache';

if (!POSTHOG_KEY) {
  throw new Error('Add your Posthog Key to the .env file');
}

if (!POSTHOG_HOST) {
  throw new Error('Add your Posthog Host to the .env file');
}

// Clear feature flag cache on page reload
if (typeof window !== 'undefined') {
  const navigationEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
  const isReload = navigationEntries?.at(0)?.type === 'reload';

  if (isReload) {
    sessionStorage.removeItem(FEATURE_FLAG_CACHE_KEY);
  }
}

export const posthogClient = posthog.init(config.env.VITE_PUBLIC_POSTHOG_KEY, {
  api_host: config.env.VITE_PUBLIC_POSTHOG_HOST,
  defaults: '2025-05-24',
  persistence: 'sessionStorage',
  autocapture: false,
  capture_pageview: false,
  capture_pageleave: false,
  disable_session_recording: true,
  disable_scroll_properties: true,
  disable_web_experiments: true,
  disable_surveys: true,
  disable_compression: false,
  session_recording: {
    recordCrossOriginIframes: false,
  },
  loaded: posthog => {
    posthog.setPersonPropertiesForFlags({}, false);

    posthog.onFeatureFlags(() => {
      // Get all feature flags from PostHog
      const allPosthogFlags = posthog.featureFlags.getFlagsWithDetails();

      // Collect feature flags which we use in the broker portal
      const brokerPortalFeatureFlags = Object.values(PosthogFeatureFlag);

      const cachedFlags = Object.entries(allPosthogFlags).reduce((acc, [flagKey, flagValue]) => {
        // Only cache flags that are defined in our PosthogFeatureFlag enum
        if (brokerPortalFeatureFlags.includes(flagKey as PosthogFeatureFlag)) {
          acc[flagKey] = flagValue.enabled;
        }
        return acc;
      }, {} as Record<string, boolean>);

      sessionStorage.setItem(FEATURE_FLAG_CACHE_KEY, JSON.stringify(cachedFlags));
    });
  },
});

type PosthogProviderProps = {
  children: ReactNode;
  isTest?: boolean;
};

export const PosthogProvider = ({ children, isTest = false }: PosthogProviderProps) => {
  if (isTest) {
    return <BasePostHogProvider client={posthogClient}>{children}</BasePostHogProvider>;
  }

  return (
    <BasePostHogProvider client={posthogClient}>
      <PosthogLoadingWrapper>{children}</PosthogLoadingWrapper>
    </BasePostHogProvider>
  );
};

function getCachedFeatureFlag(flag: PosthogFeatureFlag): boolean {
  const cache = sessionStorage.getItem(FEATURE_FLAG_CACHE_KEY);

  if (!cache) {
    return false;
  }

  const parsed = JSON.parse(cache);
  return parsed[flag] ?? false;
}

function useFeatureFlag(flag: PosthogFeatureFlag): boolean {
  return getCachedFeatureFlag(flag);
}

export const useIsTasksSystemEnabled = () => {
  return useFeatureFlag(PosthogFeatureFlag.TASKS_SYSTEM);
};

export const usePosthogSendEvent = () => {
  const posthog = usePostHog();

  return (eventName: string, properties?: Record<string, any>) => {
    posthog.capture(eventName, properties);
  };
};
